/**
 * useTrialExtension
 * Shared state for the "extend your free trial" offer. Combines billing data
 * (trialing subscription), the current user (billing owner), the build-time
 * switches (on by default, no PostHog; white label builds excluded) and the
 * trial extension store. Every surface (popup, banner, top bar, Overview card)
 * reads from here, so they always agree on availability.
 */

import { useCallback, useEffect, useMemo } from "react";
import { useAuth } from "@finitless/shared/contexts";
import { useApiStore, useUserStore } from "@finitless/shared/stores";
import { appConfig } from "@finitless/shared/config";
import { useBilling } from "./useBilling";
import { useTrialExtensionStore } from "../stores/trialExtensionStore";
import {
  buildTrialExtensionBookingUrl,
  findTrialSubscription,
  getTrialExtensionConfig,
  getTrialExtensionEligibility,
  removeBookedParamFromUrl,
  safeStorageGet,
  safeStorageSet,
  trialExtensionStorageKeys,
} from "../lib/trialExtension";

/** How often the eligibility clock is refreshed while the tab is open. */
export const TRIAL_EXTENSION_CLOCK_MS = 60 * 1000;

const persistBooked = (projectKey) =>
  safeStorageSet(
    "local",
    trialExtensionStorageKeys.booked(projectKey),
    new Date().toISOString(),
  );

export const useTrialExtension = () => {
  const user = useUserStore((state) => state.user);
  const apiProject = useApiStore((state) => state.project);
  const { isInitialized: authInitialized } = useAuth();
  const {
    project,
    subscriptions,
    isLoading: billingLoading,
    isError: billingError,
  } = useBilling();

  const now = useTrialExtensionStore((s) => s.now);
  const isModalOpen = useTrialExtensionStore((s) => s.isModalOpen);
  const modalSource = useTrialExtensionStore((s) => s.modalSource);
  const justBookedProjectKey = useTrialExtensionStore(
    (s) => s.justBookedProjectKey,
  );
  const bookedProjects = useTrialExtensionStore((s) => s.bookedProjects);
  const bannerDismissedProjects = useTrialExtensionStore(
    (s) => s.bannerDismissedProjects,
  );
  const storeOpenBooking = useTrialExtensionStore((s) => s.openBooking);
  const closeBooking = useTrialExtensionStore((s) => s.closeBooking);
  const storeMarkBooked = useTrialExtensionStore((s) => s.markBooked);
  const storeDismissBanner = useTrialExtensionStore((s) => s.dismissBanner);

  // The API project slug identifies the project for storage; the /current code
  // (same value in practice) identifies it in the booking via utm_content.
  const projectKey = apiProject || null;
  const projectCode = project?.code || apiProject || "";
  // Billing /current resolved for the current project (its query is keyed by it).
  const resolvedProjectCode =
    !billingLoading && !billingError ? project?.code || null : null;

  // Build-time switch (VITE_TRIAL_EXTENSION_OFFER_ENABLED): `enabled`, on by
  // default; booking URL and days.
  const baseConfig = useMemo(() => getTrialExtensionConfig(), []);

  // White label builds only (appConfig.is_white_label). The per-project
  // `white_label_module` config does not count: the offer targets the billing
  // owner (Ordering's direct customer) and trial plans include that module.
  const isWhiteLabel = Boolean(appConfig?.is_white_label);

  const config = useMemo(
    () => ({
      ...baseConfig,
      is_white_label: isWhiteLabel,
    }),
    [baseConfig, isWhiteLabel],
  );

  const bookedInMemory = Boolean(projectKey && bookedProjects[projectKey]);
  const booked = useMemo(
    () =>
      bookedInMemory ||
      Boolean(
        projectKey &&
        safeStorageGet("local", trialExtensionStorageKeys.booked(projectKey)),
      ),
    [bookedInMemory, projectKey],
  );

  const dismissedInMemory = Boolean(
    projectKey && bannerDismissedProjects[projectKey],
  );
  const bannerDismissed = useMemo(
    () =>
      dismissedInMemory ||
      Boolean(
        projectKey &&
        safeStorageGet(
          "session",
          trialExtensionStorageKeys.bannerDismissed(projectKey),
        ),
      ),
    [dismissedInMemory, projectKey],
  );

  const trialSubscription = useMemo(
    () => findTrialSubscription(subscriptions),
    [subscriptions],
  );

  const eligibility = getTrialExtensionEligibility({
    config,
    user,
    trialSubscription,
    booked,
    now,
  });
  const { days, daysRemaining } = eligibility;
  // Never decide (nor auto-open the popup) from partial data: wait until auth
  // has settled the project (a saved session's project may still be in place
  // before `?project=` is applied) and the user and billing data that decide
  // eligibility have loaded for it (billing queries are keyed by project).
  const ready = Boolean(
    authInitialized && user && projectKey && !billingLoading && !billingError,
  );
  const available = ready && eligibility.available;

  const embedDomain = typeof window === "undefined" ? "" : window.location.host;

  const bookingUrl = useMemo(
    () =>
      buildTrialExtensionBookingUrl({
        baseUrl: config.booking_url,
        user,
        projectCode,
        embedDomain,
      }),
    [config.booking_url, user, projectCode, embedDomain],
  );

  const fallbackUrl = useMemo(
    () =>
      buildTrialExtensionBookingUrl({
        baseUrl: config.booking_url,
        user,
        projectCode,
      }),
    [config.booking_url, user, projectCode],
  );

  const openBooking = useCallback(
    (source) => storeOpenBooking(source),
    [storeOpenBooking],
  );

  const dismissBanner = useCallback(() => {
    if (!projectKey) return;
    safeStorageSet(
      "session",
      trialExtensionStorageKeys.bannerDismissed(projectKey),
      "1",
    );
    storeDismissBanner(projectKey);
  }, [projectKey, storeDismissBanner]);

  const markBooked = useCallback(() => {
    if (!projectKey) return;
    persistBooked(projectKey);
    storeMarkBooked(projectKey);
  }, [projectKey, storeMarkBooked]);

  return {
    available,
    booked,
    justBooked: Boolean(projectKey && justBookedProjectKey === projectKey),
    days,
    daysRemaining,
    bookingUrl,
    fallbackUrl,
    projectKey,
    resolvedProjectCode,
    isModalOpen,
    modalSource,
    bannerDismissed,
    openBooking,
    closeBooking,
    dismissBanner,
    markBooked,
  };
};

/**
 * App-level side effects, mounted once (by TrialExtensionModal):
 * - Refreshes the eligibility clock every minute and when the tab becomes visible.
 * - `trial_extension_booked=1` in the boot URL marks its project as booked once
 *   the real project is known: auth initialized (it applies `?project=`), billing
 *   /current resolved for the current project, and that project matches the one
 *   the URL named (if any). Until then it stays pending in memory and in the
 *   address bar (a failed /current can be retried, even by reloading). The param
 *   leaves the address bar once applied, or once auth settled on a project other
 *   than the one the URL named, so a reload (without `?project=`) can't apply it
 *   elsewhere; it then stays pending in memory for that project.
 * - Auto-opens the booking modal at most once per browser session per project,
 *   only once the offer is available.
 */
export const useTrialExtensionAutoPopup = ({
  available,
  booked,
  projectKey,
  resolvedProjectCode,
}) => {
  const { isInitialized: authInitialized } = useAuth();
  const bookedFromUrl = useTrialExtensionStore((s) => s.bookedFromUrl);
  const bookedFromUrlProject = useTrialExtensionStore(
    (s) => s.bookedFromUrlProject,
  );
  const popupHandled = useTrialExtensionStore((s) =>
    Boolean(projectKey && s.popupHandledProjects[projectKey]),
  );
  const setNow = useTrialExtensionStore((s) => s.setNow);
  const consumeBookedFromUrl = useTrialExtensionStore(
    (s) => s.consumeBookedFromUrl,
  );
  const markBooked = useTrialExtensionStore((s) => s.markBooked);
  const markPopupHandled = useTrialExtensionStore((s) => s.markPopupHandled);
  const openBooking = useTrialExtensionStore((s) => s.openBooking);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const tick = () => setNow(Date.now());
    const handleVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    tick();
    const intervalId = window.setInterval(tick, TRIAL_EXTENSION_CLOCK_MS);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [setNow]);

  useEffect(() => {
    if (!projectKey) return;

    if (bookedFromUrl) {
      // Until auth settles, the project may still be a stale saved one.
      if (!authInitialized) return;
      const matchesUrlProject =
        !bookedFromUrlProject ||
        bookedFromUrlProject === projectKey ||
        bookedFromUrlProject === resolvedProjectCode;
      if (!matchesUrlProject) {
        // Auth settled on another project: keep the marker pending in memory
        // for the project the URL named, but not in the address bar.
        removeBookedParamFromUrl();
      } else if (!resolvedProjectCode) {
        // Billing /current still loading or failed: keep it pending (memory and
        // address bar) so a later successful load applies it.
        return;
      } else {
        consumeBookedFromUrl();
        removeBookedParamFromUrl();
        if (!booked) {
          persistBooked(projectKey);
          markBooked(projectKey, { fromCalendar: false });
        }
        return;
      }
    }

    if (!available || popupHandled) return;
    markPopupHandled(projectKey);

    const key = trialExtensionStorageKeys.popupShown(projectKey);
    if (safeStorageGet("session", key)) return;

    safeStorageSet("session", key, "1");
    openBooking("popup");
  }, [
    authInitialized,
    available,
    booked,
    bookedFromUrl,
    bookedFromUrlProject,
    consumeBookedFromUrl,
    markBooked,
    markPopupHandled,
    openBooking,
    popupHandled,
    projectKey,
    resolvedProjectCode,
  ]);
};
